import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface PlanRow {
  readonly auditPlanId: string;
  readonly planCode: string;
  readonly planName: string;
  readonly auditType: string;
  readonly fiscalYear: string;
  readonly status: string;
}

export function M21AuditPlanningPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<PlanRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [planCode, setPlanCode] = useState('');
  const [planName, setPlanName] = useState('');
  const [auditType, setAuditType] = useState('');
  const [fiscalYear, setFiscalYear] = useState('');
  const [planStartDate, setPlanStartDate] = useState('');
  const [planEndDate, setPlanEndDate] = useState('');

  function load(): void {
    apiRequest<{ data: PlanRow[] }>('/v1/audit/plans', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: PlanRow[] }>('/v1/audit/plans', { token })
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setActionError(null);
    if (!planCode.trim() || !planName.trim() || !auditType.trim() || !fiscalYear.trim()
      || !planStartDate || !planEndDate) {
      setActionError(t('m21.auditPlanning.validationRequired'));
      return;
    }
    setBusyId('create');
    try {
      await apiRequest('/v1/audit/plans', {
        method: 'POST',
        token,
        body: {
          planCode: planCode.trim(),
          planName: planName.trim(),
          auditType: auditType.trim(),
          fiscalYear: fiscalYear.trim(),
          planStartDate,
          planEndDate,
        },
      });
      setPlanCode(''); setPlanName(''); setAuditType('');
      setFiscalYear(''); setPlanStartDate(''); setPlanEndDate('');
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function approve(id: string): Promise<void> {
    setActionError(null);
    setBusyId(`approve-${id}`);
    try {
      await apiRequest(`/v1/audit/plans/${id}/approve`, { method: 'POST', token });
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m21.auditPlanning.loading')}</p>;
  return (
    <main aria-label="audit-planning">
      <h1>{t('m21.auditPlanning.title')}</h1>

      <form aria-label="audit-plan-create-form" onSubmit={handleCreate}>
        <label>{t('m21.auditPlanning.formCode')}<input aria-label="Plan code" value={planCode} onChange={(e) => setPlanCode(e.target.value)} /></label>
        <label>{t('m21.auditPlanning.formName')}<input aria-label="Plan name" value={planName} onChange={(e) => setPlanName(e.target.value)} /></label>
        <label>{t('m21.auditPlanning.formType')}<input aria-label="Audit type" value={auditType} onChange={(e) => setAuditType(e.target.value)} /></label>
        <label>{t('m21.auditPlanning.formFiscalYear')}<input aria-label="Fiscal year" value={fiscalYear} onChange={(e) => setFiscalYear(e.target.value)} /></label>
        <label>{t('m21.auditPlanning.formStart')}<DateInput aria-label="Plan start date" type="date" value={planStartDate} onValueChange={(nextValue) => setPlanStartDate(nextValue)} /></label>
        <label>{t('m21.auditPlanning.formEnd')}<DateInput aria-label="Plan end date" type="date" value={planEndDate} onValueChange={(nextValue) => setPlanEndDate(nextValue)} /></label>
        <button type="submit" disabled={busyId === 'create'}>{t('m21.auditPlanning.createButton')}</button>
      </form>
      {actionError && <p role="alert">{actionError}</p>}

      <table aria-label="audit-plans-table">
        <thead><tr><th>{t('m21.auditPlanning.colCode')}</th><th>{t('m21.auditPlanning.colName')}</th><th>{t('m21.auditPlanning.colType')}</th><th>{t('m21.auditPlanning.colFiscalYear')}</th><th>{t('m21.auditPlanning.colStatus')}</th><th>{t('m21.auditPlanning.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.auditPlanId}>
              <td>{p.planCode}</td>
              <td>{p.planName}</td>
              <td>{p.auditType}</td>
              <td>{p.fiscalYear}</td>
              <td>{p.status}</td>
              <td>
                {p.status.toUpperCase() !== 'APPROVED' && (
                  <button
                    type="button"
                    aria-label={`approve-${p.auditPlanId}`}
                    disabled={busyId === `approve-${p.auditPlanId}`}
                    onClick={() => approve(p.auditPlanId)}
                  >{t('m21.auditPlanning.approve')}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
